/**
 * Block Renderer
 *
 * Single Responsibility: Renders different types of content blocks
 * Open/Closed Principle: Can be extended with new renderers without modifying existing code
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ContentBlock } from '../parsers/MarkdownParser';
import { IBlockRenderer, TextProcessorOptions } from './IBlockRenderer';
import { BoldTextProcessor } from '../processors/BoldTextProcessor';

export class BlockRenderer implements IBlockRenderer {
  private textProcessor: BoldTextProcessor;

  constructor(textProcessor?: BoldTextProcessor) {
    this.textProcessor = textProcessor || new BoldTextProcessor();
  }

  render(
    block: ContentBlock,
    key: string,
    blockIndex: number,
    options?: TextProcessorOptions
  ): React.ReactNode {
    switch (block.type) {
      case 'heading':
        return this.renderHeading(block, key, blockIndex, options);

      case 'nested-list':
        return this.renderNestedList(block, key, options);

      case 'paragraph':
      default:
        return this.renderParagraph(block, key, options);
    }
  }

  private renderHeading(
    block: ContentBlock,
    key: string,
    blockIndex: number,
    options?: TextProcessorOptions
  ): React.ReactNode {
    return (
      <h3
        key={key}
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.lg,
          fontWeight: typography.weight.semibold,
          color: typographyColors.primary,
          lineHeight: '1.6',
          margin: 0,
          marginTop: blockIndex > 0 ? spacing[2] : 0,
        }}
      >
        {this.textProcessor.process(block.content as string, key, options)}
      </h3>
    );
  }

  private renderParagraph(
    block: ContentBlock,
    key: string,
    options?: TextProcessorOptions
  ): React.ReactNode {
    return (
      <p
        key={key}
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.base,
          fontWeight: typography.weight.regular,
          color: typographyColors.primary,
          lineHeight: '1.7',
          margin: 0,
        }}
      >
        {this.textProcessor.process(block.content as string, key, options)}
      </p>
    );
  }

  private renderNestedList(
    block: ContentBlock,
    key: string,
    options?: TextProcessorOptions
  ): React.ReactNode {
    const listItems = block.content as {
      text: string;
      children?: string[];
    }[];

    return (
      <div
        key={key}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
        }}
      >
        {listItems.map((item, itemIndex) => (
          <div key={`${key}-item-${itemIndex}`}>
            {/* Parent bullet */}
            <div
              style={{
                display: 'flex',
                gap: spacing[2],
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.base,
                  fontWeight: typography.weight.regular,
                  color: typographyColors.primary,
                  lineHeight: '1.4',
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              >
                •
              </span>
              <div
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.base,
                  fontWeight: typography.weight.regular,
                  color: typographyColors.primary,
                  lineHeight: '1.4',
                  flex: 1,
                }}
              >
                {this.textProcessor.process(
                  item.text,
                  `${key}-item-${itemIndex}`,
                  options
                )}
              </div>
            </div>

            {/* Nested children */}
            {item.children && item.children.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[2],
                  marginLeft: spacing[6],
                  marginTop: spacing[2],
                }}
              >
                {item.children.map((child, childIndex) => (
                  <div
                    key={`${key}-item-${itemIndex}-child-${childIndex}`}
                    style={{
                      display: 'flex',
                      gap: spacing[2],
                      alignItems: 'flex-start',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: fontPrimary,
                        fontSize: typography.size.base,
                        fontWeight: typography.weight.regular,
                        color: typographyColors.primary,
                        lineHeight: '1.4',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    >
                      •
                    </span>
                    <div
                      style={{
                        fontFamily: fontPrimary,
                        fontSize: typography.size.base,
                        fontWeight: typography.weight.regular,
                        color: typographyColors.primary,
                        lineHeight: '1.4',
                        flex: 1,
                      }}
                    >
                      {this.textProcessor.process(
                        child,
                        `${key}-item-${itemIndex}-child-${childIndex}`,
                        options
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}
